import React, { useCallback, useEffect, useState } from 'react';
import { useT, useLocale } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import FormField from '../components/ui/FormField';
import Input from '../components/ui/Input';
import EmptyState from '../components/ui/EmptyState';
import PayoutDestinationCard from '../components/payouts/PayoutDestinationCard';
import MonthlyPilgrimageCard from '../components/events/MonthlyPilgrimageCard';
import { useApi } from '../api/client';
import { getSettings, updateReservationFee } from '../api/settings';
import { formatCurrency } from '../utils/format';

const INTEGRATIONS = [
  { key: 'gatewayConfigured', labelKey: 'settings_page.integration_gateway', hintKey: 'settings_page.integration_gateway_hint', icon: '💳' },
  { key: 'webhookConfigured', labelKey: 'settings_page.integration_webhook', hintKey: 'settings_page.integration_webhook_hint', icon: '🔔' },
  { key: 'mailConfigured', labelKey: 'settings_page.integration_mail', hintKey: 'settings_page.integration_mail_hint', icon: '✉️' },
];

export default function SettingsPage() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { hasPermission, hasRole } = useAuth();
  const canManagePayouts = hasRole('admin') && hasPermission('payouts:manage');
  const canManageSettings = hasRole('admin') && hasPermission('settings:manage');

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fee, setFee] = useState('');
  const [feeError, setFeeError] = useState('');
  const [savingFee, setSavingFee] = useState(false);

  const load = useCallback(async () => {
    if (!useApi) return;
    setLoading(true);
    try {
      const data = await getSettings();
      setSettings(data);
      setFee(String(data?.reservationFeePerPerson ?? ''));
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  if (!useApi) {
    return (
      <Card title={t('pages.settings.title')} subtitle={t('pages.settings.subtitle')}>
        <EmptyState icon="🔌" title={t('common.api_required')} />
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card title={t('pages.settings.title')} subtitle={t('pages.settings.subtitle')}>
        <EmptyState title={loading ? t('common.loading') : t('common.no_data')} />
      </Card>
    );
  }

  const handleSaveFee = async (e) => {
    e?.preventDefault?.();
    const value = Number(fee);
    if (fee === '' || !Number.isInteger(value) || value < 0 || value > 100000) {
      setFeeError(t('settings_page.fee_invalid'));
      return;
    }
    setSavingFee(true);
    try {
      const next = await updateReservationFee(value);
      setSettings(next);
      setFee(String(next.reservationFeePerPerson ?? value));
      toast.success(t('settings_page.toast_fee_saved', { fee: formatCurrency(value, locale) }));
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setSavingFee(false);
    }
  };

  const feeDirty = String(settings.reservationFeePerPerson ?? '') !== fee;

  return (
    <div>
      <Card title={t('settings_page.integrations_title')} subtitle={t('settings_page.integrations_subtitle')}>
        <div className="health-list">
          {INTEGRATIONS.map((item) => {
            const ok = Boolean(settings[item.key]);
            return (
              <div key={item.key} className="health-row">
                <span className="health-row__icon" aria-hidden>{item.icon}</span>
                <div className="health-row__text">
                  <div className="health-row__label">{t(item.labelKey)}</div>
                  <div className="health-row__hint">{t(item.hintKey)}</div>
                </div>
                <Badge tone={ok ? 'active' : 'completed'}>
                  {ok ? t('settings_page.configured') : t('settings_page.missing')}
                </Badge>
              </div>
            );
          })}
        </div>
        <div className="form-hint" style={{ marginTop: 12 }}>{t('common.server_secret_hint')}</div>
      </Card>

      <MonthlyPilgrimageCard canEdit={canManageSettings} />

      <div className="split-grid">
        <Card title={t('settings_page.fee_title')} subtitle={t('settings_page.fee_subtitle')}>
          <form onSubmit={handleSaveFee} noValidate>
            <FormField
              label={t('settings_page.fee_label')}
              htmlFor="fee-per-person"
              error={feeError}
              hint={t('settings_page.fee_hint')}
            >
              <div className="input-suffix">
                <Input
                  id="fee-per-person"
                  type="number"
                  min={0}
                  max={100000}
                  step={1}
                  value={fee}
                  onChange={(e) => { setFee(e.target.value); if (feeError) setFeeError(''); }}
                  invalid={Boolean(feeError)}
                />
                <span className="input-suffix__unit">{t('common.currency_rwf')}</span>
              </div>
            </FormField>
            <Button type="submit" variant="gold" loading={savingFee} disabled={!feeDirty}>{t('common.save')}</Button>
          </form>
        </Card>

        <PayoutDestinationCard
          msisdn={settings.payoutMsisdn}
          name={settings.payoutName}
          providerId={settings.payoutProviderId}
          canManage={canManagePayouts}
          mailConfigured={Boolean(settings.mailConfigured)}
          onChanged={(overview) => setSettings((prev) => ({
            ...prev,
            payoutMsisdn: overview?.payoutMsisdn,
            payoutName: overview?.payoutName,
            payoutProviderId: overview?.payoutProviderId,
          }))}
        />
      </div>
    </div>
  );
}
